import React, { useEffect, useRef, useState } from "react";
import Background from "../BackGround/background";
import "./mpin.css";
import OtpInput from "react-otp-input";
import { useDispatch, useSelector } from "react-redux";
import authService from "../../services/authService";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams } from "react-router-dom";
import { decodeJWTPayload } from "../../utils/tokenValidation";

import {
  loginEvent,
  logout,
  setOtpData,
  setSessionTime,
  switchUserEvent,
  setStatus,
} from "../../slices/authSlice";
import flowBackEndServices2 from "../../services/flowBackendServices";
import { RootState } from "../../store";
import { LoginPayload, LoginResponse } from "../../models/login";
import { getDeviceID, getExtraQueryparam, renameAuthParam } from "../common/parseQueryParam";
import RiskDisclosure from "../LoginForm/RiskDisclosure";
import { DEVICE_ID_KEY, VORTEX_APPLICATION_ID } from "../../utils/config";
import { STATUSES } from '../../comman/status';

const MpinInput: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const defaultAppId = process.env.REACT_APP_TV_APPLICATION_ID;

  // Redux state
  const { user, data, themeData } = useSelector((state: RootState) => state.authUser);

  // Component state
  const [otp, setOtp] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasNavigated, setHasNavigated] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);
  const [showDisclosure, setShowDisclosure] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [kra, setKra] = useState<number>(0);
  const [showDot, setShowDot] = useState(false);
  const [aeroUrl, setAeroUrl] = useState<string | null>(null);
  const tvTerminalId = process.env.REACT_APP_TV_APPLICATION_ID

  const otpInputRef = useRef<any>(null);
  const [searchParams] = useSearchParams();

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth > 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Initial load validation
  useEffect(() => {
    if (hasNavigated) return;

    const currentPath = window.location.pathname;
    if (currentPath !== "/MPin") return;

    const useRefresh = localStorage.getItem("useRefresh");
    const persistedData = localStorage.getItem('PERSIST_OBJECT');
    const comingFromReset = sessionStorage.getItem("COMING_FROM_RESET_MPIN");

    const MfaObject = localStorage.getItem("TV_ACS_TOKEN_REFRESH")
    if (MfaObject) { 
      const persisted = JSON.parse(MfaObject); 
      if (persisted.mfaType && persisted.mfaType == "qr") { 
        handleCompleteTokenFailure(); 
        return; 
      } 
    }

    // Handle reset flow data restoration
    if (comingFromReset && !data) {
      const recentOTPData = sessionStorage.getItem("RECENT_OTP_DATA");
      if (recentOTPData) {
        try {
          const parsedOTPData = JSON.parse(recentOTPData);
          dispatch(setOtpData(parsedOTPData));
          return;
        } catch (error) {
          sessionStorage.removeItem("RECENT_OTP_DATA");
          sessionStorage.removeItem("COMING_FROM_RESET_MPIN");
        }
      }
    }

    // Validate we have necessary data to stay on MPIN page
    const hasRefreshFlow = useRefresh && persistedData;
    const hasAccessToken = data && (data.token || data.access_token);

    if (!hasRefreshFlow && !hasAccessToken && !comingFromReset) {
      setHasNavigated(true);
      clearStorageAndRedirect();
    }
  }, [data, hasNavigated, navigate, dispatch]);

  // Auto-submit when OTP length is 4
  useEffect(() => {
    if (otp.length === 4) {
      handleOnClick();
    }
  }, [otp]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const currentPath = window.location.pathname;
      if (currentPath !== "/apps") {
        sessionStorage.removeItem("COMING_FROM_RESET_MPIN");
        sessionStorage.removeItem("RECENT_OTP_DATA");
      }
    };
  }, []);

  // *** MAIN AUTHENTICATION HANDLER ***
  const handleOnClick = async () => {
    const params = localStorage.getItem("Q_PARAM_ALL");
    const applicationId = params?.split("=");
    let appId: string = ""
    if (applicationId) {
      appId = applicationId[1]
    }
    const currentPath = window.location.pathname;
    if (currentPath?.toLowerCase() !== "/mpin") return;

    const useRefresh = localStorage.getItem("useRefresh");
    const persistedData = localStorage.getItem('PERSIST_OBJECT');
    const comingFromReset = sessionStorage.getItem("COMING_FROM_RESET_MPIN");

    try {
      setIsLoading(true);

      // Force normal flow if coming from reset
      if (comingFromReset || (appId && appId.startsWith("dev_"))) {
        await executeNormalFlow();
        return;
      }

      // Try refresh flow first if available
      if (useRefresh && persistedData) {
        const parsedData = JSON.parse(persistedData);
        const refreshExpiresAt = parsedData.refresh_expires_at;

        if (refreshExpiresAt) {
          await executeRefreshFlow();
          return;
        }
      }

      // Default to normal flow
      await executeNormalFlow();

    } catch (error: any) {
      console.error("Authentication error:", error);
      if (error.response?.status === 401) {
        toast.error("Your session has expired. Please login again.");
        handleCompleteTokenFailure();
      } else {
        toast.error("An unexpected error occurred.");
        resetOtp();
      }
    } finally {
      setIsLoading(false);
    }
  };

  // *** REFRESH FLOW EXECUTION ***
  const executeRefreshFlow = async () => {
    const deviceId = await getDeviceID();
    const persistObject = localStorage.getItem("PERSIST_OBJECT");
    let ob = JSON.parse(persistObject || '{}');
    try {
      const response = await flowBackEndServices2.refreshAccessToken(
        {
          mpin: otp,
          deviceId: deviceId,
          identifier: ob?.client_code
        },
      );
      if (response?.status === 200 && response.data?.status === "success") {
        console.log("✅ Refresh flow successful");
        await handleTokenSuccess(response, "refresh", false);
      } else if (response.data?.message.toLowerCase().includes('invalid mpin')) {
        toast.error("Invalid MPIN. Please try again.");
        resetOtp();
      }
      else {
        console.log("Refresh flow failed - trying normal flow");
        await executeNormalFlow();
      }

    } catch (refreshError: any) {
      if (refreshError.response?.status === 401) {
        console.log("Refresh token expired - falling back to normal flow");
        try {
          await executeNormalFlow();
        } catch (normalError: any) {
          if (normalError.response?.status === 401) {
            console.log("Both tokens failed - forcing re-login");
            toast.error("Your session has expired. Please login again.");
            handleCompleteTokenFailure();
            return;
          }
          throw normalError;
        }
      } else {
        throw refreshError;
      }
    }
  };

  // *** NORMAL FLOW EXECUTION ***
  const executeNormalFlow = async () => {
    if (!data || (!data.token && !data.access_token)) {
      toast.error("No authentication data available. Please login again.");
      clearStorageAndRedirect();
      return;
    }

    const authToken = data.token || data.access_token;

    try {
      let response;

      if (themeData?.verifySSO) {
        response = await flowBackEndServices2.secondFactorSso(
          { mpin: otp },
          authToken
        );
      } else {
        response = await flowBackEndServices2.secondFactor(
          { mpin: otp },
          authToken
        );
      }

      console.log(response.status, response.data.status)
      if (response?.status === 200 && response.data?.status === "success") {
        console.log("✅ Normal flow successful");
        await handleTokenSuccess(response, "normal", themeData?.verifySSO);
      } else {
        const errorMsg = response?.data?.message || "Authentication failed";
        if (errorMsg.toLowerCase().includes('invalid mpin')) {
          toast.error("Invalid MPIN. Please try again.");
          resetOtp();
        } else {
          toast.error(errorMsg);
          resetOtp();
        }
      }

    } catch (normalError: any) {
      if (normalError.response?.status === 401 || normalError.response?.status === 403) {
        throw normalError; // Let calling function handle complete failure
      } else {
        const errorMsg = normalError.response?.data?.message || "An error occurred";
        if (errorMsg.toLowerCase().includes('invalid mpin')) {
          toast.error("Invalid MPIN. Please try again.");
          resetOtp();
        } else {
          toast.error(errorMsg);
          resetOtp();
        }
      }
    }
  };

  // *** UNIFIED TOKEN SUCCESS HANDLER ***
  const handleTokenSuccess = async (response: any, flowType: "refresh" | "normal", isSSO: boolean) => {
    if (isSSO) {
      let redirectUrl = response?.data?.redirectUrl ?? '';
      if (themeData?.applicationId === VORTEX_APPLICATION_ID) {
        redirectUrl = renameAuthParam(redirectUrl);
      }
      window.open(redirectUrl + getExtraQueryparam(), "_self")
      return
    }
    const persistedData = localStorage.getItem('PERSIST_OBJECT');
    const parsedPersistData = persistedData ? JSON.parse(persistedData) : {};

    // Extract tokens from response
    const ssoToken = response.data.ssoToken || response.data.data?.ssoToken;
    const accessToken = response.data.access_token || response.data.token || ssoToken;

    if (!ssoToken) {
      toast.error("Authentication failed. Please try again.");
      resetOtp();
      return;
    }

    // Store tokens in localStorage
    const newPersistObject = {
      refresh_expires_at: response.data.refresh_expires_at,
      user_name: response.data.data?.user_name || response.data.user_name || parsedPersistData.user_name,
      mobile: response.data.data?.mobile || response.data.mobile || parsedPersistData.mobile,
      access_token: accessToken,
      client_code: response.data.data?.user_id
    };
    localStorage.setItem('PERSIST_OBJECT', JSON.stringify(newPersistObject));

    const authLoginData = {
      ssoToken: ssoToken,
      tradingActive: response.data.data?.tradingActive || response.data.tradingActive || false
    };
    localStorage.setItem("AUTH_LOGIN", JSON.stringify(authLoginData));
    localStorage.setItem("useRefresh", "true");

    // Update Redux state
    dispatch(setOtpData({
      data: {
        ...response.data.data,
        access_token: accessToken,
        ssoToken: ssoToken
      },
      user_id: newPersistObject.mobile
    }));

    // Update session time
    dispatch(setSessionTime(Date.now()));

    // Clean up reset flow flags
    sessionStorage.removeItem("COMING_FROM_RESET_MPIN");
    sessionStorage.removeItem("RECENT_OTP_DATA");
    sessionStorage.setItem("COMING_FROM_MPIN", "true");

    // Check if disclosure should be shown
    // const lastShown = localStorage.getItem('last_disclosure_shown');
    if (flowType === "refresh") {
      dispatch(loginEvent({ mobile: newPersistObject.mobile, identifier: response.data.data?.identifier || null }));
      setHasNavigated(true);
      setShowDisclosure(true);
    } else {
      setUpSessionData(response.data);
    }
  };

  // *** HELPER FUNCTIONS ***
  const resetOtp = () => {
    setOtp("");
    setTimeout(() => {
      otpInputRef.current?.focusInput(0);
    }, 0);
  };

  const handleCompleteTokenFailure = () => {
    // Clear all authentication data
    localStorage.removeItem('PERSIST_OBJECT');
    localStorage.removeItem("AUTH_LOGIN");
    localStorage.removeItem("useRefresh");

    // Clear session storage
    sessionStorage.removeItem("COMING_FROM_RESET_MPIN");
    sessionStorage.removeItem("RECENT_OTP_DATA");
    sessionStorage.removeItem("COMING_FROM_MPIN");
    sessionStorage.removeItem("MPIN_NAVIGATION_SUCCESS");

    // Clear Redux state
    dispatch(switchUserEvent());
    dispatch(logout());

    // Redirect to login
    setHasNavigated(true);
    navigate("/", { replace: true });
  };

  const clearStorageAndRedirect = () => {
    localStorage.removeItem('PERSIST_OBJECT');
    localStorage.removeItem("AUTH_LOGIN");
    localStorage.removeItem("useRefresh");

    dispatch(switchUserEvent());
    dispatch(logout());

    sessionStorage.removeItem("COMING_FROM_RESET_MPIN");
    sessionStorage.removeItem("RECENT_OTP_DATA");

    navigate("/", { replace: true });
  };

  // *** RESET MPIN HANDLERS ***
  const resetMpinFunction = () => {
    // Store current context for recovery
    if (data) {
      sessionStorage.setItem("RECENT_OTP_DATA", JSON.stringify({
        data: data,
        user_id: data.mobile || data.clientCode
      }));
    }

    sessionStorage.setItem("COMING_FROM_RESET_MPIN", "true");

    const isRefreshFlow = localStorage.getItem("useRefresh");

    if (isRefreshFlow && (!themeData || themeData.applicationId === defaultAppId)) {
      handleResetMpin();
    } else {
      if (!themeData || themeData.applicationId === defaultAppId) {
        // dispatch(switchUserEvent());
        // dispatch(authService.logout());
        navigate("/ResetMPin", { replace: true });
      } else {
        navigate("/ResetMPin", { replace: true });
        handleResetMpin();
      }
    }
  };

  const handleResetMpin = () => {
    // dispatch(switchUserEvent());
    // dispatch(authService.logout());
    flowLoginApi();
  };

  const flowLoginApi = async () => {
    // Get mobile from various sources
    let mobile = "";

    try {
      const persistedData = localStorage.getItem('PERSIST_OBJECT');
      if (persistedData) {
        mobile = JSON.parse(persistedData).mobile;
      }

      if (!mobile) {
        const recentOTPData = sessionStorage.getItem("RECENT_OTP_DATA");
        if (recentOTPData) {
          const parsed = JSON.parse(recentOTPData);
          mobile = parsed.data?.mobile || parsed.user_id;
        }
      }

      if (!mobile && data) {
        mobile = data.mobile || data.clientCode;
      }

      if (!mobile) {
        toast.error("Unable to reset MPIN. Please login again.");
        clearStorageAndRedirect();
        return;
      }

    } catch (error) {
      toast.error("Unable to reset MPIN. Please login again.");
      clearStorageAndRedirect();
      return;
    }
    const deviceId = await getDeviceID();

    const dataSend: LoginPayload = {
      mobile: mobile,
      deviceId: deviceId,
      applicationId: themeData?.applicationId || process.env.REACT_APP_TV_APPLICATION_ID
    };

    setIsLoading(true);

    try {
      const value = await flowBackEndServices2.flowLogin2fo(dataSend);
      const loginResponse: LoginResponse = value.data;

      if (value.status === 200 && loginResponse.status === 'success') {
        let responseData = {
          data: { ...dataSend, ...loginResponse },
          user_id: dataSend.mobile
        };

        sessionStorage.setItem("RECENT_OTP_DATA", JSON.stringify(responseData));
        dispatch(setOtpData(responseData));

        if (loginResponse.mfaType === '' && loginResponse.token === '' && loginResponse.pwdNeedsChange) {
          navigate("/FlowForgetPassword#RESET", { state: responseData });
        } else if (loginResponse.token !== '' && loginResponse.pwdNeedsChange) {
          navigate("/PasswordNeedChange", { state: responseData });
        } else {
          sessionStorage.setItem("COMING_FROM_RESET_MPIN", "true");
          navigate("/loginOTPVerify", { state: responseData });
        }
      } else {
        toast.error(loginResponse.message || "Login failed");
      }
    } catch (error) {
      toast.error("Invalid Data");
    } finally {
      setIsLoading(false);
    }
  };

  // *** DISCLOSURE HANDLERS ***
  // Function to check if disclosure should be shown
  // const shouldShowDisclosure = () => {
  //   const lastShown = localStorage.getItem('last_disclosure_shown');
  //   console.log('=== Disclosure Check ===');
  //   console.log('Last shown date from storage:', lastShown);
  //   console.log('Type of last shown:', typeof lastShown);

  //   if (!lastShown) {
  //     console.log('No last shown date found - showing disclosure');
  //     return true;
  //   }

  //   const today = new Date();
  //   const todayStr = today.toDateString();
  //   console.log('Today\'s date:', todayStr);
  //   console.log('Type of today:', typeof todayStr);
  //   console.log('Raw comparison:', lastShown === todayStr);
  //   console.log('Length of last shown:', lastShown.length);
  //   console.log('Length of today:', todayStr.length);

  //   const shouldShow = lastShown !== todayStr;
  //   console.log('Should show disclosure:', shouldShow);
  //   console.log('=== End Disclosure Check ===');
  //   return shouldShow;
  // };

  const setUpSessionData = async (value: any) => {
    if (value.status !== "success") return;

    dispatch(setSessionTime(Date.now()));

    if (themeData?.verifySSO) {
      const params = localStorage.getItem("Q_PARAM_ALL");
      let applicationId = "";
      if (params) {
        const match = params.match(/applicationId=([^&"]+)/);
        if (match) {
          applicationId = match[1];
        }
      }
      if (applicationId && applicationId === 'at_FJcVwHkaBC"') {
        handleKRAStatus(value.kra_status);
        setShowDisclosure(true);
        setAeroUrl(value?.redirectUrl + getExtraQueryparam());
      } else {
        window.open(value?.redirectUrl + getExtraQueryparam(), "_self");
      }
      return;
    }

    // Always show disclosure for normal flow
    handleKRAStatus(value.data?.kra_status);
    setShowDisclosure(true);

    // Update Redux with session data
    const fullRedirectUrl = value?.redirect_url + getExtraQueryparam();
    const newData = {
      ...data,
      refreshToken: value.refreshToken || value.refresh_token,
      redirectURL: fullRedirectUrl,
    };

    dispatch(setOtpData({ data: newData, user_id: data?.clientCode }));
    const deviceId = await getDeviceID();
    const refreshData = {
      refreshToken: value.refreshToken || value.refresh_token,
      ssoToken: value.ssoToken,
      deviceId: deviceId,
      tradingActive: value.data?.tradingActive,
    };

    const userModify = { ...value.data, ...refreshData };

    dispatch(loginEvent({ mobile: userModify.mobile || null, identifier: userModify.identifier || null }));
    authService.saveUserSessionFlow(userModify, refreshData, dispatch);
  };

  const handleKRAStatus = (kraStatus: string) => {
    if (kraStatus === "HOLD") {
      setShowNext(true);
      setKra(1);
      setShowDot(true);
    } else if (kraStatus === "REJECTED") {
      setShowNext(true);
      setKra(0);
      setShowDot(true);
    } else {
      setShowNext(false);
    }
  };

  const acknowledge = () => {
    localStorage.setItem("acknowledge_disclosure", "false");
    sessionStorage.setItem("acknowledge_disclosure", "false");
    const app_id = localStorage.getItem("Q_PARAM_ALL")?.split("=")[1]?.replace(/"/g, "");
    if (aeroUrl) {
      window.open(aeroUrl, "_self");
    } else if (app_id && app_id.startsWith('dev_')) {
      const persistedData = localStorage.getItem('TV_ACS_TOKEN');
      const parsedPersistData = persistedData ? JSON.parse(persistedData) : {};
      const ssoToken = parsedPersistData.ssoToken || '';
      const accessToken = parsedPersistData.access_token || '';
      callApiAndRedirect(ssoToken, accessToken, app_id || '');
    } else {
      if (themeData?.applicationId === process.env.REACT_APP_APPLICATION_ID_FOR_VORTEX) {
        redirectToVOrtex()
      } else if (themeData?.applicationId === process.env.REACT_APP_APPLICATION_ID_FOR_DOCK) {
        redirectToDock()
      } else {
        navigate('/apps', { replace: true });
      }
    }
  };

  function callApiAndRedirect(ssoToken: string, accessToken: string, app_id: string) {
    const datasend = {
      ssoToken: ssoToken,
      applicationId: app_id
    };
    dispatch(setStatus(STATUSES.LOADING));
    flowBackEndServices2.getRedirecUrl(datasend, accessToken)
      .then((value) => {
        if (value?.status === 200) {
          if (value.data?.redirectUrl) {
            let fullRedirectUrl = app_id === VORTEX_APPLICATION_ID
              ? renameAuthParam(value.data.redirectUrl)
              : value.data.redirectUrl;
            fullRedirectUrl += getExtraQueryparam();
            window.open(fullRedirectUrl, '_self');
          } else {
            toast.error(`${value.data.message}`);
          }
        } else {
          localStorage.clear();
          sessionStorage.clear();
          navigate("/login", { replace: true });
          toast.error(`Token Expired Login Again`);
        }
      })
      .catch(() => {
        toast.error(`invalid Data`);
        setIsLoading(false);
        dispatch(setStatus(STATUSES.IDLE));
      })
      .finally(() => dispatch(setStatus(STATUSES.IDLE)));
  }

  const redirectToVOrtex = () => {
    let app_id = localStorage.getItem("Q_PARAM_ALL")?.split("=")[1].replace(/"/g, "");
    if (app_id === process.env.REACT_APP_APPLICATION_ID_FOR_VORTEX) {
      const persistedData = localStorage.getItem('TV_ACS_TOKEN');
      const parsedPersistData = persistedData ? JSON.parse(persistedData) : {};
      const ssoToken = parsedPersistData.ssoToken || '';
      const accessToken = parsedPersistData.access_token || '';
      callApiAndRedirect(ssoToken, accessToken, app_id || process.env.REACT_APP_APPLICATION_ID_FOR_VORTEX || '');
    } else {
      navigate('/apps', { replace: true });
    }
  }

  const redirectToDock = () => {
    let app_id = localStorage.getItem("Q_PARAM_ALL")?.split("=")[1].replace(/"/g, "")
    if (app_id === process.env.REACT_APP_APPLICATION_ID_FOR_DOCK) {
      const baseUrl = process.env.REACT_APP_DOCK__REDIRECT_URL || "";
      const query = window.location.search.startsWith("?") ? window.location.search : "?" + window.location.search;
      const authLogin = localStorage.getItem("AUTH_LOGIN");
      const ssoToken = authLogin ? JSON.parse(authLogin).ssoToken : "";
      const auth = encodeURIComponent(ssoToken);
      let finalUrl = baseUrl.replace(/\?$/, "") + query;
      finalUrl += (finalUrl.includes("?") ? "&" : "?") + "sso_auth_token=" + auth;
      finalUrl += getExtraQueryparam();
      window.location.href = finalUrl;
    } else {
      navigate('/apps', { replace: true });
    }
  }

  const moveToNext = () => {
    setShowNext(false);
  };

  const getDisclosure = () => {
    localStorage.setItem("acknowledge_disclosure", "true");
    sessionStorage.setItem("acknowledge_disclosure", "true");

    const content = (
      <>
        <div className="disclosureLayer"></div>
        <div className="disclosure" style={!isDesktop ? { width: "300px" } : {}}>
          {!showNext && <RiskDisclosure />}
          <div className="dot-container">
            {!isDesktop && <div id="first-dot" className="cr-dot"></div>}
            {showNext && <div id="second-dot" className="cr-dot"></div>}
          </div>
          <div className="discFooter">
            <div className="ackButton c-pointer" onClick={!showNext ? acknowledge : moveToNext}>
              {!showNext ? "I understand" : "Next"}
            </div>
          </div>
        </div>
      </>
    );

    return content;
  };

  // *** RENDER ***
  const persistedData = localStorage.getItem('PERSIST_OBJECT');
  const parsedData = persistedData ? JSON.parse(persistedData) : {};
  const finalUser = user?.user_name ?? parsedData.user_name ?? '';
  const useRefresh = localStorage.getItem("useRefresh");
  const GotoLogin = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/", { replace: true });
  };
  return (
    <>
      <Background>
        <div className="m-2">
          {useRefresh && persistedData && <div style={{ padding: '10px 16px 0px 16px', fontWeight: 600, textAlign: 'right' }}>
            <span className="mb-0 colorBlue c-pointer" onClick={GotoLogin}>Switch user</span>
          </div>
          }
          {(!useRefresh || !persistedData) && <div style={{ padding: '10px 16px 0px 16px', fontWeight: 600 }} className="c-pointer">
            <i onClick={GotoLogin} className="fas fa-arrow-left fas fa-lg" />
          </div>
          }
          <div className="h-100 d-flex card_padding flex-column justify-content-between align-items-center" style={{ paddingTop: '0px' }}>
            <div className="flex-fill w-100  p-5">
              <div className="d-flex flex-column justify-content-between align-items-center">
                <div className="circle mt-2">{finalUser.charAt(0)}</div>
                <div style={{ color: '#171D27' }} className="h3 fw-bolder pt-4">{finalUser}</div>
              </div>

              <div className="w-100 d-flex flex-row justify-content-between align-items-center">
                <div className="colorBlack text-center mx-auto fw-bold">
                  Enter your Rupeezy MPIN
                </div>
                {data?.mfaType === "totp" && (
                  <div className="colorBlue c-pointer">
                    Switch to TOTP
                  </div>
                )}
              </div>

              <div className="w-100 mt-2 d-flex flex-row justify-content-center align-items-center">
                <OtpInput
                  inputStyle="opt_box-hide"
                  value={otp}
                  ref={otpInputRef}
                  onChange={setOtp}
                  numInputs={4}
                  isInputNum={true}
                  isInputSecure={false}
                  shouldAutoFocus={true}
                  separator={<span />}
                />
              </div>

              <div className="d-flex mt-2 justify-content-center">
                <div onClick={resetMpinFunction} className="colorBlue c-pointer fw-bold">
                  Reset MPIN
                </div>
              </div>
            </div>
          </div>
        </div>
      </Background>

      {showDisclosure && getDisclosure()}
    </>
  );
};

export default MpinInput;